import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Heart, Search, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
export function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'About | IntelliEats';
  }, []);
  const values = [{
    icon: Search,
    title: 'Research-Driven',
    description: 'Born from a passion for researching ingredients and uncovering the hidden dangers in everyday packaged foods.'
  }, {
    icon: Heart,
    title: 'Healthier Living',
    description: 'We help you understand what is really in your food so you can make choices that support a longer, healthier life.'
  }, {
    icon: ShieldCheck,
    title: 'Personalized Awareness',
    description: 'Dietary restriction settings let you personalize your scanner and follow your own diet with confidence.'
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="pt-32 pb-24 bg-white min-h-screen" data-id="element-28">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-29">
        <header className="mb-12" data-id="element-30">
          <Link to="/" className="inline-flex items-center text-sm font-medium text-brand-600 hover:text-brand-700 mb-8 transition-colors" data-id="element-31">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-32" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-6" data-id="element-33">
            <div className="bg-brand-100 text-brand-600 p-3 rounded-2xl" data-id="element-34">
              <Sparkles className="w-8 h-8" data-id="element-35" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight" data-id="element-36">
              About the App
            </h1>
          </div>
          <p className="text-lg text-slate-600 leading-relaxed" data-id="element-37">
            Most people want to eat healthy — but understanding what's actually
            in your food shouldn't require a chemistry degree.
          </p>
        </header>

        {/* Main Story */}
        <div className="bg-gradient-to-br from-brand-50 to-accent-50/40 rounded-3xl p-8 sm:p-10 border border-brand-100 mb-12" data-id="element-38">
          <div className="space-y-5 text-slate-700 leading-relaxed text-lg" data-id="element-39">
            <p data-id="element-40">
              Most people want to eat healthy. However, when you go to a store
              and look at the ingredient label, it is full of ingredients you
              have never heard of and can't pronounce! How would you know if it
              is healthy or not?
            </p>
            <p data-id="element-41">
              This is a major problem. According to the FDA,{' '}
              <span className="font-medium text-slate-900" data-id="element-42">
                "Diet-related chronic diseases are the leading causes of death
                and disability in the U.S."
              </span>
            </p>
            <p data-id="element-43">
              This app was born out of the passion of researching, helping
              others, promoting healthy eating, and the desire to help others
              understand and know the hidden dangers of ingredients in their
              daily packaged foods — ingredients that are often linked to
              long-term health issues.
            </p>
            <p data-id="element-44">
              We have expanded our purpose by adding dietary restriction
              settings to help you personalize your own scanner and follow your
              own diet, along with getting awareness of what is in your food.
            </p>
            <p className="font-semibold text-brand-800" data-id="element-45">
              We hope this app can help you make healthier choices and lead a
              healthier life :)
            </p>
          </div>
        </div>

        {/* Values */}
        <div className="grid sm:grid-cols-3 gap-6 mb-12" data-id="element-46">
          {values.map((value, index) => <motion.div key={value.title} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: index * 0.1
        }} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-id="element-47">
              <div className="w-12 h-12 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center mb-4" data-id="element-48">
                <value.icon className="w-6 h-6" data-id="element-49" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2" data-id="element-50">{value.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed" data-id="element-51">
                {value.description}
              </p>
            </motion.div>)}
        </div>

        {/* Developer Note */}
        <div className="text-center border-t border-slate-100 pt-10" data-id="element-52">
          <p className="text-slate-500" data-id="element-53">
            Developed with care by{' '}
            <span className="font-semibold text-slate-900" data-id="element-54">Nidhi Matele</span>
          </p>
          <Link to="/#download" className="inline-flex items-center mt-6 px-6 py-3 bg-brand-600 text-white font-semibold rounded-full hover:bg-brand-700 transition-colors shadow-lg shadow-brand-200/50" data-id="element-55">
            Download IntelliEats Free
          </Link>
        </div>
      </div>
    </motion.div>;
}